import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import { OrganizationUserEntityRelationTypes, OrganizationUser } from 'interfaces';
import {
  Box, Grid, Icon, Typography,
} from '../../../../1-primative';
import {
  Button, Dialog, Form, MenuItem, SelectField, IconButton, ClientSelectField,
} from '../../../../2-component';
import { entityName } from '../../../../../util';
import { ADD_ENTITY_TO_ORG_USER } from '../../../../4-module/widgets/accessToEntity/accessToEntity.queries';

type OrgUserAccess = {
  firstName?: string,
  lastName?: string,
  entityName?: string,
  entityId?: string,
  relation?: OrganizationUserEntityRelationTypes,
};

export const GrantAccessToEntityButton = ({ organizationUser, refetch }: { organizationUser: OrganizationUser, refetch: () => void }) => {
  const { t } = useTranslation('components');
  const [open, setOpen] = useState<boolean>(false);
  const [orgUserAccess, setOrgUserAccess] = useState<OrgUserAccess>({});

  const [addEntityToOrganizationUser, { loading }] = useMutation(ADD_ENTITY_TO_ORG_USER, {
    onCompleted: () => {
      refetch();
      handleClose();
    },
  });

  const handleClose = () => {
    setOpen(false);
    setOrgUserAccess({});
  };

  const handleSubmit = () => {
    addEntityToOrganizationUser({
      variables: {
        input: {
          entityId: orgUserAccess.entityId,
          organizationUserId: organizationUser?.id,
          relation: orgUserAccess.relation,
          readOnly: false,
        },
      },
    });
  };

  const isSubmissionDisabled = !(orgUserAccess.entityId && orgUserAccess.relation) || loading;

  return (
    <>
      <IconButton onClick={(e) => {
        e.stopPropagation();
        setOpen(true);
      }}>
        <AddIcon data-testid='initiate-goal-wizard' />
      </IconButton>
      <Dialog onClick={(e) => e.stopPropagation()} fullWidth open={open} onClose={handleClose} sx={{ '.MuiDialog-container': { '.MuiPaper-root': { padding: '20px' } } }} >
        <Box display="flex" flexDirection="row" justifyContent="end">
          <Box sx={{ cursor: 'pointer' }} display="flex" onClick={handleClose}><Icon icon={CloseRoundedIcon} size='medium' /></Box>
        </Box>
        <Form onSubmit={handleSubmit}>
          <Grid container gap={2}>
            <Grid item xs={12}>
              <Typography variant='headingMedium' sx={{ mt: 1 }}>{t('accessToEntity.addButton.formTitle', { entityName: entityName(organizationUser) })}</Typography>
            </Grid>
            <Grid item xs={12}>
              <SelectField fullWidth
                label={t('accessToEntity.addButton.relationLabel')}
                value={orgUserAccess.relation || ''}
                onChange={(e: any) => setOrgUserAccess((prev) => ({ ...prev, relation: e.target.value }))}
              >
                {Object.values(OrganizationUserEntityRelationTypes).map((relation) => (
                  <MenuItem key={relation} value={relation}>{t(`accessToEntity.relations.${relation}`)}</MenuItem>
                ))}
              </SelectField>
            </Grid>
            <Grid item xs={12}>
              <ClientSelectField fullWidth
                label={t('accessToEntity.addButton.entitySearchText')}
                user={{
                  id: orgUserAccess.entityId, firstName: orgUserAccess.firstName, lastName: orgUserAccess.lastName, entityName: orgUserAccess.entityName,
                }}
                setUser={(u) => setOrgUserAccess({
                  ...orgUserAccess, entityId: u?.id, firstName: u?.firstName, lastName: u?.lastName, entityName: u?.entityName,
                })}
              />
            </Grid>
            <Grid item xs={12} mt={3} justifyContent='end' display='flex'>
              <Button type='submit' color='primary' disabled={isSubmissionDisabled} label={t('accessToEntity.addButton.grantAccessButton')} />
            </Grid>
          </Grid>
        </Form>
      </Dialog>
    </>
  );
};
